// Vector port of TournamentPrintableTable.js's `styles.header` block
// (club logo + title + Club/Location/Format/Stage/Date meta row), shared
// by every pdf*Table/pdfBracketTree page. PrintableToc.js's own header is
// visually simpler (one joined meta line, no per-field wrap) and stays a
// separate small block inside pdfToc.js rather than reusing this.
import { t } from "i18next";
import { PRINT_THEME as T } from "./printTheme";
import { getTournamentExportHeaderInfo } from "./exportUtils";
import { px2pt, px2mm, lineHeightMm, drawText, drawBadge, hLine, loadImageAsPngDataUrl, drawCircularImage } from "./pdfKit";

const TITLE_FS = px2pt(22);
const META_FS = px2pt(13);
const LOGO_MM = px2mm(56);
const LOGO_GAP_MM = px2mm(14);
const TITLE_META_GAP_MM = px2mm(8);
const META_ITEM_GAP_MM = px2mm(14);
const PAD_BOTTOM_MM = px2mm(14);
const MARGIN_BOTTOM_MM = px2mm(20);

const buildItems = (info) => {
    const items = [];
    if (info.club) items.push({ label: `${t("Club")}: `, value: info.club });
    if (info.location) items.push({ label: `${t("Location")}: `, value: info.location });
    if (info.formatStr) items.push({ label: `${t("Format")}: `, value: info.formatStr });
    if (info.stage) items.push({ label: `${t("Stage")}: `, value: info.stage, badge: true });
    if (info.dateRange) items.push({ label: `${t("Date")}: `, value: info.dateRange });
    return items;
};

// Wraps meta items left-to-right, breaking to a new line when an item
// would overflow `maxWidthMm` - mirrors the live header's `flexWrap: "wrap"`
// row. Returns the per-item x/line placement so draw() can reuse it as-is.
const layoutItems = (pdf, items, maxWidthMm) => {
    pdf.setFontSize(META_FS);
    let x = 0;
    let line = 0;
    const placed = items.map((item, idx) => {
        pdf.setFont(pdf.__pdfFontName || "PTSans", "normal");
        const labelW = pdf.getTextWidth(item.label);
        const valueW = pdf.getTextWidth(item.value);
        const badgeExtra = item.badge ? px2mm(4) : 0;
        const itemW = labelW + valueW + badgeExtra;
        const gap = x === 0 ? 0 : META_ITEM_GAP_MM;
        if (x > 0 && x + gap + itemW > maxWidthMm) {
            line += 1;
            x = 0;
        } else if (x > 0) {
            x += gap;
        }
        const placement = { ...item, x, line, labelW, valueW };
        x += itemW;
        return placement;
    });
    return { placed, lineCount: line + 1 };
};

export const preloadHeaderAssets = async (tournament) => {
    const { logo } = getTournamentExportHeaderInfo(tournament, "");
    const logoDataUrl = logo ? await loadImageAsPngDataUrl(logo) : null;
    return { logoDataUrl };
};

export const measureHeader = (pdf, tournament, stageName, maxWidthMm) => {
    const info = getTournamentExportHeaderInfo(tournament, stageName);
    const items = buildItems(info);
    const { lineCount } = layoutItems(pdf, items, maxWidthMm - LOGO_MM - LOGO_GAP_MM);
    const metaH = lineCount * lineHeightMm(META_FS, 1.5);
    const titleH = lineHeightMm(TITLE_FS, 1.15);
    const rowH = Math.max(LOGO_MM, titleH + TITLE_META_GAP_MM + metaH);
    return { heightMm: rowH + PAD_BOTTOM_MM + MARGIN_BOTTOM_MM, info };
};

export const drawHeader = (pdf, x, y, tournament, stageName, maxWidthMm, logoDataUrl) => {
    const info = getTournamentExportHeaderInfo(tournament, stageName);
    const items = buildItems(info);
    const textX = logoDataUrl ? x + LOGO_MM + LOGO_GAP_MM : x;
    const textMaxW = maxWidthMm - (logoDataUrl ? LOGO_MM + LOGO_GAP_MM : 0);
    const { placed, lineCount } = layoutItems(pdf, items, textMaxW);

    if (logoDataUrl) {
        drawCircularImage(pdf, logoDataUrl, x + LOGO_MM / 2, y + LOGO_MM / 2, LOGO_MM);
    }

    const titleH = drawText(pdf, info.name || t("Tournament"), textX, y, {
        fontSizePt: TITLE_FS, color: T.ink, maxWidthMm: textMaxW, multiplier: 1.15,
    });

    const metaY = y + titleH + TITLE_META_GAP_MM;
    const metaLh = lineHeightMm(META_FS, 1.5);
    placed.forEach((item) => {
        const itemY = metaY + item.line * metaLh + metaLh * 0.78;
        pdf.setFontSize(META_FS);
        pdf.setFont(pdf.__pdfFontName || "PTSans", "normal");
        pdf.setTextColor(T.ink);
        pdf.text(item.label, textX + item.x, itemY);
        if (item.badge) {
            drawBadge(pdf, item.value, textX + item.x + item.labelW, itemY - metaLh * 0.68, {
                fontSizePt: px2pt(12), bg: T.border, color: T.ink,
            });
        } else {
            pdf.setFont(pdf.__pdfFontName || "PTSans", "normal");
            pdf.setTextColor(T.muted);
            pdf.text(item.value, textX + item.x + item.labelW, itemY);
        }
    });

    const metaH = lineCount * metaLh;
    const rowH = Math.max(LOGO_MM, titleH + TITLE_META_GAP_MM + metaH);
    const borderY = y + rowH + PAD_BOTTOM_MM;
    hLine(pdf, x, borderY, maxWidthMm, T.border, px2mm(2));

    return borderY + MARGIN_BOTTOM_MM;
};
